import { z } from 'zod';
import { uuidSchema } from './common.js';
import { calculationBasisSchema } from './financials.js';

/**
 * Market Trends — Zod DTOs (SIL-295, W2, financials-hifi-flow-spec.md
 * slice 6 + §Market-comp disclosure policy).
 *
 * Backs `GET /api/client/market-trends` (apps/api/src/routes/client/market-trends.ts).
 * Fleet-scoped, read-only composition over existing comp/pricing tables
 * (`sku_pricing_snapshot`, `sku_mcmp`) — no new ingestion.
 *
 * Tenant boundary is application-side (comp/SKU tables are platform data,
 * no RLS): the SKU/asset-class set is derived server-side from the caller
 * org's owned assets; a caller-supplied `skuId`/`assetClassId` outside that
 * set is rejected uniformly (404 — spec "un-owned SKU -> 404/empty").
 *
 * k-anonymity floor (COMPS_K_ANONYMITY_FLOOR, financials-constants.ts):
 * median/p25/p75/count are suppressed (nulled) below the floor — this DTO
 * NEVER carries a raw sub-floor count, a per-comp row, a comp id/URL, or a
 * per-source breakdown (spec §Market-comp disclosure policy — `sx_internal`
 * stays in the underlying math but is never split out here).
 */

// ---------------------------------------------------------------------------
// Query
// ---------------------------------------------------------------------------

export const marketTrendsQuerySchema = z.object({
  /** Optional single-SKU drill-down filter — must be within the caller org's
   * owned SKU set or the request is rejected (uniform 404). */
  skuId: uuidSchema.optional(),
  /** Optional single-asset-class filter — same owned-set rejection rule. */
  assetClassId: z.string().optional(),
});
export type MarketTrendsQuery = z.infer<typeof marketTrendsQuerySchema>;

// ---------------------------------------------------------------------------
// Response
// ---------------------------------------------------------------------------

/** Bucketed freshness — never a comp-level exact timestamp (spec: "freshness
 * is bucketed"). `unknown` covers a SKU with no snapshot refresh stamp at all
 * (distinct from "over 90 days", which HAS a stamp, just an old one). */
export const marketTrendFreshnessBucketSchema = z.enum([
  'within_30_days',
  'within_60_days',
  'within_90_days',
  'over_90_days',
  'unknown',
]);
export type MarketTrendFreshnessBucket = z.infer<typeof marketTrendFreshnessBucketSchema>;

/** One point of the 12-18mo trend line — a calendar month bucket over
 * `sku_mcmp` windows (all condition bands composed together, spec "no new
 * ingestion" — this is composition over the existing rolling-window table,
 * not a new market-math model). `medianPriceUsd` is null when the bucket's
 * summed comp count is below the k-anonymity floor OR the SKU had no comps
 * that month — the two are indistinguishable on the wire by design (never
 * disclose the raw sub-floor count, spec §Market-comp disclosure policy). */
export const marketTrendPointSchema = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/, 'expected YYYY-MM'),
  medianPriceUsd: z.coerce.number().nullable(),
});
export type MarketTrendPoint = z.infer<typeof marketTrendPointSchema>;

/** Current p25/median/p75 band + comps count, from the latest materialized
 * `sku_pricing_snapshot` row for the SKU (same source `v_sku_catalog`'s
 * current-band columns use). Suppressed as a unit below the k-anonymity
 * floor — `insufficientDepth: true` and every numeric field null. */
export const marketTrendCurrentBandSchema = z.object({
  medianUsd: z.coerce.number().nullable(),
  p25Usd: z.coerce.number().nullable(),
  p75Usd: z.coerce.number().nullable(),
  /** Null when suppressed (below k-floor) — never the true sub-floor count. */
  compsCount: z.number().int().nonnegative().nullable(),
  freshnessBucket: marketTrendFreshnessBucketSchema,
  insufficientDepth: z.boolean(),
});
export type MarketTrendCurrentBand = z.infer<typeof marketTrendCurrentBandSchema>;

/** One SKU's market-trend series, tagged with its owning asset class (spec
 * slice 6: "for each asset class / top SKU the org owns" — this DTO iterates
 * at SKU granularity, each row carrying its class for UI grouping; comps are
 * SKU-scoped in the underlying tables, so a genuine class-level aggregate
 * would require inventing new cross-SKU math this ticket's "no new
 * ingestion / read composition over existing views" scope deliberately
 * avoids). */
export const marketTrendSeriesSchema = z.object({
  skuId: uuidSchema,
  assetClassId: z.string(),
  assetClassName: z.string(),
  label: z.string(),
  /** Count of the caller org's OWN assets on this SKU — drives "top SKU"
   * ranking, not a comp count. */
  ownedAssetCount: z.number().int().nonnegative(),
  /** 12-18mo trailing trend, oldest first. */
  trend: z.array(marketTrendPointSchema),
  current: marketTrendCurrentBandSchema,
  /** Org's own average net book value per unit for this SKU (from
   * `v_client_asset_book_value`), null when no `asset_financial` coverage. */
  orgBookValuePerUnitUsd: z.coerce.number().nullable(),
  /** current.medianUsd - orgBookValuePerUnitUsd; null when either side is
   * null (including when current is k-floor-suppressed). */
  deltaVsBookValueUsd: z.coerce.number().nullable(),
  basis: calculationBasisSchema,
});
export type MarketTrendSeries = z.infer<typeof marketTrendSeriesSchema>;

export const marketTrendsResponseSchema = z.object({
  series: z.array(marketTrendSeriesSchema),
  generatedAt: z.string(),
});
export type MarketTrendsResponse = z.infer<typeof marketTrendsResponseSchema>;
